import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Select,
  DatePicker, Tabs, Progress, message, Statistic
} from 'antd';
import {
  BarChartOutlined, DownloadOutlined, ReloadOutlined, UserOutlined,
  EnvironmentOutlined, TagsOutlined, ClockCircleOutlined, TeamOutlined
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import {
  getAttendanceReport, getLocationSummary, getCategorySummary,
  getHourlyTraffic, getEvents
} from '../api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const COLORS = ['#0F1D6B', '#C41E3A', '#52c41a', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

export default function ConferenceReportMaster() {
  const [events,      setEvents]      = useState([]);
  const [eventId,     setEventId]     = useState(null);
  const [date,        setDate]        = useState(dayjs());
  const [attendance,  setAttendance]  = useState([]);
  const [locations,   setLocations]   = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [hourly,      setHourly]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [activeTab,   setActiveTab]   = useState('attendance');

  useEffect(() => {
    getEvents().then(r => {
      const evList = r.data.data || [];
      setEvents(evList);
      const active = evList.find(e => e.status === 'active');
      if (active) setEventId(active.id);
    }).catch(() => {});
  }, []);

  const loadReports = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    Promise.all([
      getAttendanceReport({ event_id: eventId }),
      getLocationSummary({ event_id: eventId }),
      getCategorySummary({ event_id: eventId }),
      getHourlyTraffic({ event_id: eventId, date: date?.format('YYYY-MM-DD') }),
    ]).then(([a, l, c, h]) => {
      setAttendance(a.data.data || []);
      setLocations(l.data.data || []);
      setCategories(c.data.data || []);
      setHourly(h.data.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eventId, date]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const present = attendance.filter(d => d.current_location).length;

  const exportExcel = async () => {
    const wb  = new ExcelJS.Workbook();
    const ws1 = wb.addWorksheet('Attendance');
    ws1.addRow(['Name', 'RFID Tag', 'Category', 'Company', 'Current Location', 'Last Seen', 'Presence']);
    ws1.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
    ws1.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    attendance.forEach(d => ws1.addRow([
      d.delegate_name, d.rfid_tag, d.category, d.company || '',
      d.current_location || '',
      d.last_seen ? dayjs(d.last_seen).format('DD-MMM HH:mm') : '',
      d.current_location ? 'Present' : 'Away',
    ]));
    const ws2 = wb.addWorksheet('Locations');
    ws2.addRow(['Location', 'Code', 'Type', 'Capacity', 'Current Count', 'Total Scans']);
    ws2.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
    ws2.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    locations.forEach(l => ws2.addRow([l.location_name, l.location_code, l.location_type, l.capacity, l.current_delegates, l.total_scans]));
    ws1.columns.forEach(c => { c.width = 22; });
    ws2.columns.forEach(c => { c.width = 18; });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `conference_report_${dayjs().format('YYYYMMDD')}.xlsx`);
    message.success('Report exported');
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const ev  = events.find(e => e.id === eventId);
    doc.setFontSize(14);
    doc.text(`Conference Report: ${ev?.event_name || ''}`, 14, 16);
    doc.setFontSize(9);
    doc.text(`Generated: ${dayjs().format('DD MMM YYYY HH:mm')} | Total: ${attendance.length}`, 14, 23);
    doc.autoTable({
      startY: 28,
      head: [['Name', 'RFID Tag', 'Category', 'Company', 'Last Location', 'Presence']],
      body: attendance.map(d => [
        d.delegate_name, d.rfid_tag?.slice(-10), d.category, d.company || '',
        d.current_location || '-', d.current_location ? 'Present' : 'Away',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 29, 107] },
    });
    doc.save(`conference_report_${dayjs().format('YYYYMMDD')}.pdf`);
    message.success('PDF exported');
  };

  const attendanceCols = [
    { title: '#', width: 44, render: (_, __, i) => i + 1 },
    { title: 'Name', dataIndex: 'delegate_name', render: v => <Text strong>{v}</Text>, sorter: (a, b) => a.delegate_name.localeCompare(b.delegate_name) },
    { title: 'RFID Tag', dataIndex: 'rfid_tag', render: v => <Text code style={{ fontSize: 10 }}>{v?.slice(-12)}</Text> },
    { title: 'Category', dataIndex: 'category', render: v => v ? <Tag>{v}</Tag> : '-', width: 110 },
    { title: 'Company', dataIndex: 'company', render: v => v || '-' },
    { title: 'Last Location', dataIndex: 'current_location', render: v => v ? <Tag color="blue">{v}</Tag> : '-', width: 140 },
    { title: 'Last Seen', dataIndex: 'last_seen', render: v => v ? dayjs(v).format('HH:mm:ss') : '-', width: 90 },
    { title: 'Presence', width: 90, render: (_, d) => <Tag color={d.current_location ? 'green' : 'default'}>{d.current_location ? 'Present' : 'Away'}</Tag> },
  ];

  const locationCols = [
    { title: 'Location', dataIndex: 'location_name', render: v => <Text strong>{v}</Text> },
    { title: 'Code', dataIndex: 'location_code', render: v => <Tag>{v}</Tag>, width: 120 },
    { title: 'Type', dataIndex: 'location_type', render: v => <Tag color="purple">{v}</Tag>, width: 110 },
    { title: 'Capacity', dataIndex: 'capacity', width: 90, align: 'center' },
    { title: 'Count', dataIndex: 'current_delegates', render: v => <Text strong style={{ fontSize: 16 }}>{v}</Text>, width: 80, align: 'center' },
    {
      title: 'Occupancy', width: 180,
      render: (_, l) => {
        if (!l.capacity) return '-';
        const pct = Math.min(100, ((l.current_delegates || 0) / l.capacity) * 100);
        return <Progress percent={Math.round(pct)} size="small" strokeColor={pct > 90 ? '#f5222d' : pct > 70 ? '#faad14' : '#52c41a'} />;
      }
    },
    { title: 'Total Scans', dataIndex: 'total_scans', width: 100, align: 'center' },
    { title: 'Online Readers', dataIndex: 'online_readers', width: 110, align: 'center' },
  ];

  const tabItems = [
    {
      key: 'attendance',
      label: <Space size={4}><UserOutlined />Attendance</Space>,
      children: (
        <Card style={{ borderRadius: 10 }}>
          <Table columns={attendanceCols} dataSource={attendance} rowKey="id"
            loading={loading} size="small" scroll={{ x: 900 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t} delegates` }} />
        </Card>
      ),
    },
    {
      key: 'location',
      label: <Space size={4}><EnvironmentOutlined />Location Summary</Space>,
      children: (
        <div>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {locations.map(l => {
              const pct = l.capacity ? Math.min(100, ((l.current_delegates || 0) / l.capacity) * 100) : 0;
              return (
                <Col xs={12} sm={8} md={6} key={l.id}>
                  <Card size="small" style={{ borderRadius: 10, textAlign: 'center', borderTop: `4px solid ${l.current_delegates > 0 ? '#1B2B8C' : '#d9d9d9'}` }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0F1D6B', lineHeight: 1 }}>{l.current_delegates}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, margin: '4px 0' }}>{l.location_name}</div>
                    <Text type="secondary" style={{ fontSize: 10 }}>Cap: {l.capacity || '∞'}</Text>
                    {l.capacity > 0 && <Progress percent={Math.round(pct)} size="small" style={{ marginTop: 6 }} showInfo={false} />}
                  </Card>
                </Col>
              );
            })}
          </Row>
          <Card style={{ borderRadius: 10 }}>
            <Table columns={locationCols} dataSource={locations} rowKey="id" loading={loading} size="small" pagination={false} />
          </Card>
        </div>
      ),
    },
    {
      key: 'category',
      label: <Space size={4}><TagsOutlined />Category Breakdown</Space>,
      children: (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card title="Category Distribution" style={{ borderRadius: 10 }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categories.map(c => ({ name: c.name, value: c.total || 0 }))}
                    cx="50%" cy="50%" outerRadius={110} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Present vs Away by Category" style={{ borderRadius: 10 }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categories.map(c => ({ name: c.name, Present: c.present || 0, Away: (c.total || 0) - (c.present || 0) }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Present" fill="#52c41a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Away"    fill="#ff4d4f" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: 'hourly',
      label: <Space size={4}><ClockCircleOutlined />Hourly Traffic</Space>,
      children: (
        <div>
          <Card size="small" style={{ marginBottom: 16, borderRadius: 10 }}>
            <Space>
              <Text>Traffic Date:</Text>
              <DatePicker value={date} onChange={setDate} />
            </Space>
          </Card>
          <Card title="Hourly RFID Scan Traffic" style={{ borderRadius: 10 }}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={Array.from({ length: 24 }, (_, h) => {
                const found = hourly.find(d => d.hour === h);
                return {
                  hour: `${String(h).padStart(2, '0')}:00`,
                  scans:     found?.scan_count     || 0,
                  delegates: found?.unique_delegates || 0,
                };
              })}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="scans"     stroke="#0F1D6B" strokeWidth={2} dot={false} name="Total Scans" />
                <Line type="monotone" dataKey="delegates" stroke="#52c41a" strokeWidth={2} dot={false} name="Unique Delegates" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <BarChartOutlined style={{ marginRight: 8 }} />Conference Report Master
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Attendance, location, category and hourly traffic reports</Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Select Event">
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
            <Button icon={<DownloadOutlined />} onClick={exportExcel}>Excel</Button>
            <Button icon={<DownloadOutlined />} onClick={exportPDF}>PDF</Button>
            <Button icon={<ReloadOutlined />} onClick={loadReports} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* Stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          { title: 'Total Registered',    value: attendance.length,                                    icon: <TeamOutlined />,        color: '#0F1D6B' },
          { title: 'Present',             value: present,                                               icon: <UserOutlined />,        color: '#52c41a' },
          { title: 'Away / Outside',      value: attendance.length - present,                           icon: <UserOutlined />,        color: '#999' },
          { title: 'Locations Occupied',  value: locations.filter(l => (l.current_delegates || 0) > 0).length, icon: <EnvironmentOutlined />, color: '#722ed1' },
        ].map((s, i) => (
          <Col xs={12} md={6} key={i}>
            <Card style={{ borderRadius: 10, borderTop: `4px solid ${s.color}` }} size="small">
              <Space>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: '#0F1D6B' }}>{s.value ?? 0}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{s.title}</div>
                </div>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" items={tabItems} />
    </div>
  );
}
